import React from 'react';
import { KatAccordion } from '@amzn/katal-react';
import { useAppSelector } from 'src/redux/hooks';
import { AppState } from 'src/redux/store';
import { selectSerialLockFrontierIndex } from 'src/redux/slices/learningModules';
import { LearningPathwaySection } from 'src/types/state/learningPathway';
import ModuleSectionItemV2, { CredentialAssessment } from './ModuleSectionItemV2';

interface LearningPathwayAccordionV2Props {
  sections: LearningPathwaySection[];
  onModuleSelect: (moduleId: string) => void;
  isLocalized?: boolean;
  hideSectionLabels?: boolean;
  assessment?: CredentialAssessment;
  enforceSerialProgression?: boolean;
  footer?: React.ReactNode;
}

/**
 * LearningPathwayAccordionV2 Component
 *
 * Renders an accordion view for the credential experience's sections and modules,
 * consuming the V2 card/section components. Each section can be expanded to show its
 * modules, and the credential assessment is appended to the final section.
 *
 * When serial progression is enforced, modules and the assessment unlock in order across
 * the whole credential: everything at or after the first not-yet-complete module is gated.
 * The frontier is computed over the flattened module order so gating spans section
 * boundaries. A module completed out of order stays gated (showing its completed pill)
 * until the frontier reaches it.
 */
const LearningPathwayAccordionV2: React.FC<LearningPathwayAccordionV2Props> = ({
  sections,
  onModuleSelect,
  isLocalized = false,
  hideSectionLabels = false,
  assessment,
  enforceSerialProgression = false,
  footer,
}) => {
  const orderedModuleIds = sections.reduce<string[]>(
    (acc, section) => acc.concat(section.learningModuleIds),
    [],
  );
  const frontierIndex = useAppSelector(
    (state: AppState) => selectSerialLockFrontierIndex(state, orderedModuleIds),
  );

  let moduleOffset = 0;

  return (
    <KatAccordion className="mx-auto w-full">
      {sections.map((section, index) => {
        const sectionModuleStartIndex = moduleOffset;
        moduleOffset += section.learningModuleIds.length;
        return (
          <ModuleSectionItemV2
            key={section.sectionId}
            section={section}
            index={index}
            onModuleSelect={onModuleSelect}
            expanded={index === 0}
            isLocalized={isLocalized}
            hideSectionLabel={hideSectionLabels}
            assessment={index === sections.length - 1 ? assessment : undefined}
            enforceSerialProgression={enforceSerialProgression}
            sectionModuleStartIndex={sectionModuleStartIndex}
            serialLockFrontierIndex={frontierIndex}
            footer={index === sections.length - 1 ? footer : undefined}
          />
        );
      })}
    </KatAccordion>
  );
};

export default LearningPathwayAccordionV2;
